<template>
  <div class="bg-gray-100 dark-bg-custom p-6 pt-0">
    <div class="sticky top-0 z-50 pt-4 bg-[#f3f4f6] dark-bg-custom">
      <div class="flex justify-between">
        <p class="text-md" v-if="isUploadCase">
          Verify Case - <b>({{ editSubCase?.name }})</b>
        </p>
        <p class="text-md" v-else>
          Create Case - <b>({{ useCase }})</b>
        </p>
        <v-btn plain @click="onCancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>
    <v-divider class="mb-4"></v-divider>
    <div class="mb-5 w-full" v-if="branches.length">
      <v-select
        v-model="selectedBranch"
        :items="branches"
        item-value="_id"
        item-text="branchName"
        label="Link Branch (Link your mapping with branch)"
        :disabled="!!isUploadCase"
      ></v-select>
    </div>

    <v-form ref="form" v-model="valid" lazy-validation>
      <v-expansion-panels v-model="panel" flat>
        <!-- Field Mapping Panel -->
        <v-expansion-panel>
          <v-expansion-panel-header class="px-2">
            Case-Field Mapping
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="mb-5 w-full flex justify-between align-center">
              <v-text-field
                v-model="caseType.value"
                label="Case Name"
                required
                :rules="caseType.rules"
                :disabled="!!isUploadCase || !!existingCaseType"
                hide-details
              ></v-text-field>
            </div>
            <div class="grid grid-cols-3 gap-3">
              <div
                v-for="(key, i) in orgKeys"
                :key="i"
                class="mb-5 w-full border border-blue-200 rounded-md p-3"
              >
                <div class="flex justify-between">
                  <v-text-field
                    v-model="key.value"
                    :label="key.text"
                    required
                    :rules="key.rules"
                    :disabled="!!isUploadCase"
                    hide-details
                  >
                  </v-text-field>
                  <span class="red--text" v-if="key.required">*</span>
                </div>

                <div class="flex justify-around mb-3">
                  <v-switch
                    v-model="key.visibility"
                    label="Visibility"
                    class=""
                    hide-details
                  ></v-switch>
                  <v-switch
                    v-model="key.required"
                    label="Required"
                    class=""
                    hide-details
                  ></v-switch>
                </div>
              </div>
            </div>
            <div class="flex">
              <v-divider class="my-3 border border-5"></v-divider>
              <p class="mx-4 text-sm text-gray-500">Additional Config</p>
              <v-divider class="my-3 border border-5"></v-divider>
            </div>
            <div class="grid grid-cols-3 gap-3">
              <v-select
                v-model="selectedAmountKey"
                label="Amount Collection Field"
                :items="amountMappingOptions"
                item-text="value"
                item-value="key"
                :disabled="!!isUploadCase"
                hide-details
              >
                <template v-slot:append>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        class="info-icon"
                        :color="$vuetify.theme.currentTheme.primary"
                      >
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <span
                      >Collection amount calculation will be done based on the
                      mapped field.</span
                    >
                  </v-tooltip>
                </template>
              </v-select>
              <v-checkbox label="TAT" v-model="tat"></v-checkbox>
              <v-checkbox
                label="Case Start Date/Time"
                v-model="caseStartDate"
              ></v-checkbox>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- customer addresses -->
        <v-expansion-panel>
          <v-expansion-panel-header class="px-2"
            >Customer Details Mapping</v-expansion-panel-header
          >
          <v-expansion-panel-content>
            <!-- Static Fields -->
            <div class="grid grid-cols-3 gap-4">
              <div class="mb-5 w-full">
                <v-text-field
                  v-model="staticFields.crn"
                  label="CRN No (Unique Identifier)"
                  required
                  :rules="staticFields.crnRules"
                  :disabled="!!isUploadCase"
                  hide-details
                >
                </v-text-field>
              </div>
              <div class="mb-5 w-full">
                <v-text-field
                  v-model="staticFields.customerFullName"
                  label="Customer Name"
                  required
                  :rules="staticFields.customerFullNameRules"
                  :disabled="!!isUploadCase"
                  hide-details
                ></v-text-field>
              </div>
              <div class="mb-5 w-full">
                <v-text-field
                  v-model="staticFields.customerMobile"
                  label="Customer Mobile Number"
                  :disabled="!!isUploadCase"
                  hide-details
                ></v-text-field>
              </div>
              <div class="mb-5 w-full">
                <v-text-field
                  v-model="staticFields.customerEmail"
                  label="Customer Email"
                  :disabled="!!isUploadCase"
                  hide-details
                ></v-text-field>
              </div>
            </div>

            <v-alert v-if="alertMessage" type="warning" dense text>
              {{ alertMessage }}
            </v-alert>
            <!-- Dynamic Address Fields -->
            <div v-for="(address, index) in addresses" :key="index">
              <v-divider class="mb-2"></v-divider>
              <div class="grid grid-cols-3 gap-4 mb-5">
                <div class="col-span-3 flex items-center justify-between mb-4">
                  <span class="flex">
                    <h3 class="text-md font-semibold">
                      Address {{ index + 1 }}
                    </h3>
                    <v-checkbox
                      :color="$vuetify.theme.currentTheme.primary"
                      v-model="address.default"
                      label="Set as default"
                      class="mt-0 pt-0 ml-2"
                      @change="setDefault(index)"
                    ></v-checkbox>
                  </span>
                  <v-icon
                    @click="removeAddress(index)"
                    class="cursor-pointer"
                    color="red"
                    v-show="addresses.length > 1"
                    >mdi-delete</v-icon
                  >
                </div>

                <v-select
                  v-model="address.typeOfAddress"
                  :items="getFilteredAddressTypes(index)"
                  label="Type of Address"
                  :disabled="!!isUploadCase"
                  class="mb-5 w-full"
                  hide-details
                ></v-select>

                <div
                  v-for="(field, key) in addressFields"
                  :key="key"
                  class="mb-5 w-full"
                >
                  <v-text-field
                    v-model="address[key].displayName"
                    :label="field.text"
                    :placeholder="field.text"
                    :disabled="!!isUploadCase"
                    hide-details
                    @input="validateAddress"
                  ></v-text-field>
                </div>
              </div>
            </div>

            <div
              class="flex justify-end"
              v-show="addresses.length < addressTypes.length"
            >
              <v-btn
                :color="$vuetify.theme.currentTheme.primary"
                class="white--text"
                small
                @click="addAddress"
                >Add Another Address</v-btn
              >
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- Extra Column Panel -->
        <v-expansion-panel>
          <v-expansion-panel-header class="px-2"
            >Extra Column</v-expansion-panel-header
          >
          <v-expansion-panel-content>
            <div class="grid grid-cols-3 gap-4">
              <div
                v-for="(item, i) in extraColumns"
                :key="item.key"
                class="mb-5 w-full relative"
              >
                <v-autocomplete
                  v-model="item.value"
                  :items="getFilteredItems(i)"
                  item-text="text"
                  item-value="value"
                  :search-input.sync="item.search"
                  :loading="loading"
                  :label="item.text"
                  @input="updateColumnValue(i, $event)"
                  :disabled="!!isUploadCase"
                  hide-details
                >
                  <template v-slot:selection="{ item }">
                    {{ item.value }}
                  </template>
                  <template v-slot:append>
                    <v-icon
                      color="red"
                      class="mb-0.5"
                      size="20"
                      :disabled="!!isUploadCase"
                      @click="removeExtraColumn(i)"
                    >
                      mdi-delete-outline
                    </v-icon>
                  </template>
                </v-autocomplete>
              </div>
            </div>
            <div class="flex justify-end my-2">
              <v-btn
                elevation="2"
                small
                @click="addExtraColumn"
                v-if="!isUploadCase"
                :color="$vuetify.theme.currentTheme.primary"
                class="white--text"
              >
                <v-icon>mdi-plus</v-icon>
                Add Column
              </v-btn>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- Case Status Panel -->
        <v-expansion-panel v-model="panel" flat>
          <v-expansion-panel-header class="px-2"
            >Case Outcomes</v-expansion-panel-header
          >
          <v-expansion-panel-content>
            <v-text-field
              v-model="caseOutcomeStatus"
              label="Case Outcome Column"
              :disabled="!!isUploadCase"
              required
            >
              <template v-slot:append>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      class="info-icon"
                      :color="$vuetify.theme.currentTheme.primary"
                    >
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <span
                    >The binded text will be displayed in the teams/cases table
                    and the same text will be used of update/create the case
                    data.</span
                  >
                </v-tooltip>
              </template>
            </v-text-field>
            <CaseOutcomeStatus
              :categories="categories"
              :removeCategory="removeCategory"
              :addCategory="addCategory"
              :isUploadCase="isUploadCase"
              v-model="caseOutcomeStatus"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel v-model="panel" flat v-show="!isUploadCase">
          <v-expansion-panel-header class="px-2">
            Set Questionnaire (for case outcomes)
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-tabs v-model="tabs" align-with-title>
              <v-tabs-slider color="blue"></v-tabs-slider>
              <v-tab v-for="item in catOptionTabs" :key="item" class="ml-0">
                {{ item }}
              </v-tab>
            </v-tabs>
            <v-tabs-items v-model="tabs">
              <v-tab-item v-for="(item, index) in catOptionTabs" :key="item">
                <div class="mt-4">
                  <Questionnaire
                    :questionTypes="allowedQuestionsTypes"
                    @questions="(val) => handleQuestions(val, index)"
                    :orgMapping="'orgMapping'"
                    :subCaseQuestions="subCaseQuestions[item]"
                    :outcomeQuestions="
                      getFilteredOutcomeQuestions(subCaseQuestions, item)
                    "
                    @clonedQuestions="
                      (val) => handleCloneAction(val, item, 'subCaseQuestions')
                    "
                  />
                </div>
              </v-tab-item>
            </v-tabs-items>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel v-model="panel" flat v-show="!isUploadCase">
          <v-expansion-panel-header class="px-2">
            Set Customer Feedback Questions
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-tabs v-model="tabs" align-with-title>
              <v-tabs-slider color="blue"></v-tabs-slider>
              <v-tab v-for="item in catOptionTabs" :key="item" class="ml-0">
                {{ item }}
              </v-tab>
            </v-tabs>
            <v-tabs-items v-model="tabs">
              <v-tab-item v-for="(item, index) in catOptionTabs" :key="item">
                <div class="mt-4">
                  <Questionnaire
                    :questionTypes="allowedQuestionsTypesCustomerFeedback"
                    @questions="(val) => handleFeedback(val, index)"
                    :orgMapping="'orgMapping'"
                    :subCaseQuestions="customerFeedback[item]"
                    :outcomeQuestions="
                      getFilteredOutcomeQuestions(customerFeedback, item)
                    "
                    @clonedQuestions="
                      (val) => handleCloneAction(val, item, 'customerFeedback')
                    "
                  />
                </div>
              </v-tab-item>
            </v-tabs-items>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel v-model="panel" flat>
          <v-expansion-panel-header class="px-2"
            >Case Status</v-expansion-panel-header
          >
          <v-expansion-panel-content>
            <div>
              <v-alert type="info" dense text>
                The case status column value should be
                <strong>OPEN, CLOSE, or IN_PROGRESS</strong>.
              </v-alert>
              <v-text-field
                v-model="caseStatusName"
                label="Case Status (OPEN/CLOSE/IN_PROGRESS)"
                :disabled="!!isUploadCase"
                required
              ></v-text-field>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel v-model="panel" flat>
          <v-expansion-panel-header class="px-2"
            >Customize Tabs</v-expansion-panel-header
          >
          <v-expansion-panel-content>
            <div>
              <v-alert type="info" dense text>
                Below mentioned text will be visible as the tabs title.
                <strong>(Ex: Case, Visit etc.)</strong>.
              </v-alert>
              <div class="grid grid-cols-3 gap-4">
                <div
                  v-for="(key, i) in tableTabKeys"
                  :key="i"
                  class="mb-5 w-full"
                >
                  <v-text-field
                    v-model="key.value"
                    :label="key.text"
                    :disabled="!!isUploadCase"
                    required
                  ></v-text-field>
                </div>
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-form>

    <div class="flex justify-end gap-6 mt-4" v-if="!isUploadCase">
      <div class="flex justify-end gap-4">
        <v-btn elevation="2" small text @click="onCancel"> Cancel </v-btn>
        <v-btn depressed @click="onSubmit" color="primary" elevation="2" small>
          Save as draft
        </v-btn>
      </div>
    </div>
    <AlertPopUp
      :showConfirmationDialog="showConfirmationClone"
      :confirmationTitle="'Confirmation for existing questions'"
      :confirmationMessage="'Questions already exist in this case outcome type, you want to keep the previous questions or replace with new questions'"
      :confirmationActionText="'Replace with new questions'"
      :cancelActionText="'Keep previous questions'"
      :confirmationActionColor="'green'"
      :performAction="cloneNewQuestions"
      :cancel="cloneWithExistingQuestions"
    />
  </div>
</template>

<script>
import Questionnaire from "./OrgConfigComponents/Questionnaire";
import CaseOutcomeStatus from "./OrgConfigComponents/CaseOutcomeStatus";
import AlertPopUp from "@/components/AlertPopUp";
import { mergeOptions } from "@/utils/common";
const defaultOrgKeys = [
  {
    text: "Case No (Unique Identifier)",
    key: "caseNo",
    value: "",
    visibility: true,
    rules: [(v) => !!v || "Case No is required"],
    required: true,
  },
  {
    text: "Type of loan",
    key: "typeOfLoan",
    value: "",
    visibility: true,
    required: false,
  },
  {
    text: "Bucket",
    key: "bkt",
    value: "",
    visibility: true,
    required: false,
  },
  {
    text: "Product Info",
    key: "productInfo",
    value: "",
    visibility: true,
    required: false,
  },
  {
    text: "POS (Number)",
    key: "pos",
    value: "",
    visibility: true,
    required: false,
  },
  {
    text: "EMI Amount (Number)",
    key: "emiAmount",
    value: "",
    visibility: true,
    required: false,
  },
  {
    text: "Tenure (Number)",
    key: "tenure",
    value: "",
    visibility: true,
    required: false,
  },
  {
    text: "Task Title",
    key: "taskTitle",
    value: "",
    visibility: true,
    rules: [(v) => !!v || "Task Title is required"],
    required: true,
  },
  {
    text: "Task Description",
    key: "taskDescription",
    value: "",
    visibility: true,
    required: false,
  },
  {
    text: "Assigned Person",
    key: "assignedTo",
    value: "",
    visibility: true,
    required: false,
  },
  {
    text: "EMI Start Date (Date)",
    key: "emiStartDate",
    value: "",
    visibility: false,
    required: false,
  },
  {
    text: "EMI End Date (Date)",
    key: "emiEndDate",
    value: "",
    visibility: false,
    required: false,
  },
  {
    text: "Next EMI Due Date (Date)",
    key: "nextEmiDate",
    value: "",
    visibility: false,
    required: false,
  },
];

export default {
  name: "OrgMapping",
  components: { Questionnaire, CaseOutcomeStatus, AlertPopUp },
  data() {
    return {
      userOrg: this.$storage.getUniversal("user"),
      caseType: {
        text: "Case Name",
        key: "caseType",
        value: "",
        rules: [(v) => !!v || "Case Name is required"],
      },
      caseStartDate: true,
      tat: true,
      tabs: 0,
      orgKeys: [...defaultOrgKeys],
      tableTabKeys: [
        { text: "Cases", key: "caseTable", value: "Case" },
        { text: "Visits", key: "visitTable", value: "Visit" },
        {
          text: "Case Analytics",
          key: "caseAnalyticsTable",
          value: "Case Analytics",
        },
        {
          text: "Call History",
          key: "callHistoryTable",
          value: "Call History",
        },
        {
          text: "Customers",
          key: "customerTable",
          value: "Customers",
        },
      ],
      staticFields: {
        crn: "",
        customerFullName: "",
        customerMobile: "",
        customerEmail: "",
        crnRules: [(v) => !!v || "CRN No is required"],
        customerFullNameRules: [(v) => !!v || "Customer Name is required"],
      },
      addressTypes: [
        "CURRENT_ADDRESS",
        "PERMANENT_ADDRESS",
        "RESIDENTIAL_ADDRESS",
        "OFFICE_ADDRESS",
        "BILLING_ADDRESS",
        "MAILING_ADDRESS",
      ],
      addressFields: {
        addressLine1: { key: "", text: "Address Line 1" },
        addressLine2: { key: "", text: "Address Line 2" },
        landMark: { key: "", text: "Land Mark" },
        city: { key: "", text: "City" },
        state: { key: "", text: "State" },
        country: { key: "", text: "Country" },
        pinCode: { key: "", text: "Pincode" },
      },
      addresses: [
        {
          typeOfAddress: "CURRENT_ADDRESS",
          addressLine1: { key: "", displayName: "" },
          addressLine2: { key: "", displayName: "" },
          landMark: { key: "", displayName: "" },
          city: { key: "", displayName: "" },
          state: { key: "", displayName: "" },
          country: { key: "", displayName: "" },
          pinCode: { key: "", displayName: "" },
        },
      ],
      loading: false,
      caseOutcomeStatus: "",
      caseStatusName: "",
      panel: 0,
      extraColumns: [],
      branches: [],
      selectedBranch: "",
      selectedAmountKey: null,
      valid: true,
      subCaseQuestions: {},
      customerFeedback: {},
      alertMessage: "",
      showConfirmationClone: false,
      clonedValue: null,
      clonedKey: null,
      cloneTarget: null,
      selectedMendatoryFields: null,
      categories: [
        {
          categoryName: "",
          options: [],
          optionsList: [],
          assignedTemplates: {},
        },
      ],
      existingCaseType: null,
    };
  },
  props: {
    useCase: String,
    enabledSubCases: {
      type: Array,
      default: () => [],
    },
    editSubCase: Object,
    cloneSubCase: Object,
    isNewSubCase: Boolean,
    isUploadCase: Boolean,
    allowedQuestionsTypes: Array,
    allowedQuestionsTypesCustomerFeedback: Array,
  },
  computed: {
    amountMappingOptions() {
      return this.orgKeys
        .filter((key) => ["pos", "emiAmount", "tenure"].includes(key.key))
        .map((key) => ({
          text: key.text,
          value: key.value || key.text,
          key: key.key,
        }));
    },
    catOptionTabs() {
      return mergeOptions(this.categories) || [];
    },
    mendatoryFields() {
      return this.orgKeys
        .filter((key) =>
          [
            "pos",
            "emiAmount",
            "tenure",
            "typeOfLoan",
            "productInfo",
            "taskTitle",
            "taskDescription",
            "bkt",
          ].includes(key.key)
        )
        .map((key) => ({
          text: key.text,
          value: key.value || key.text,
          key: key.key,
        }));
    },
  },
  methods: {
    removeExtraColumn(index) {
      this.extraColumns.splice(index, 1);
    },
    getFilteredItems(index) {
      const search =
        this.extraColumns[index].search || this.extraColumns[index].text;
      if (search) {
        const baseItems = ["STRING", "NUMBER", "DATE"];
        return baseItems.map((type) => ({
          text: `${search} - ${type}`,
          value: `${search} - ${type}`,
        }));
      }
      return [];
    },

    updateColumnValue(index, value) {
      this.extraColumns[index].value = value;
    },
    addExtraColumn() {
      const newColumnIndex = this.extraColumns.length;
      const newColumn = {
        text: `Column ${newColumnIndex + 1}`,
        key: `column_${newColumnIndex + 1}`,
        value: "",
        search: "",
      };

      this.extraColumns = [...this.extraColumns, newColumn];
    },

    removeCategory(index) {
      this.categories.splice(index, 1);
    },
    removeAddress(index) {
      this.checkForDuplicateAddressLine1();
      this.addresses.splice(index, 1);
    },
    addAddress() {
      const newAddress = {
        typeOfAddress: "",
        addressLine1: { key: "", displayName: "" },
        addressLine2: { key: "", displayName: "" },
        landMark: { key: "", displayName: "" },
        city: { key: "", displayName: "" },
        state: { key: "", displayName: "" },
        country: { key: "", displayName: "" },
        pinCode: { key: "", displayName: "" },
      };
      this.addresses.push(newAddress);
      this.checkForDuplicateAddressLine1();
    },
    setDefault(index) {
      this.addresses.forEach((address, i) => {
        if (i !== index) {
          address.default = false;
        }
      });
    },

    getFilteredAddressTypes(currentIndex) {
      const selectedTypes = this.addresses
        .filter((_, index) => index !== currentIndex)
        .map((address) => address.typeOfAddress);

      return this.addressTypes.filter((type) => !selectedTypes.includes(type));
    },

    async onSubmit() {
      let fieldMappings = {};
      let additionalColumns = {};
      let tableTabMapping = {};
      const isFormValid = this.$refs.form.validate();
      if (!isFormValid) {
        this.$toast.error(
          "Validation failed. Please fill all the required field in the form."
        );
        return;
      }

      const formatKey = (displayName) => {
        return (displayName || "").trim().replace(/\s+/g, "_").toLowerCase();
      };

      fieldMappings["crn"] = {
        key: formatKey(this.staticFields?.crn),
        displayName: this.staticFields?.crn,
        visibility: true,
      };
      fieldMappings["customerFullName"] = {
        key: formatKey(this.staticFields?.customerFullName),
        displayName: this.staticFields?.customerFullName,
        visibility: true,
      };
      fieldMappings["customerMobile"] = {
        key: formatKey(this.staticFields?.customerMobile),
        displayName: this.staticFields?.customerMobile,
        visibility: true,
      };
      fieldMappings["customerEmail"] = {
        key: formatKey(this.staticFields?.customerEmail),
        displayName: this.staticFields?.customerEmail,
        visibility: true,
      };
      fieldMappings["tat"] = {
        key: "tat",
        displayName: "",
        visibility: this.tat,
      };
      fieldMappings["caseStartDate"] = {
        key: "caseStartDate",
        displayName: "",
        visibility: this.caseStartDate,
      };

      const userDefinedCaseStatus = this.categories;

      this.orgKeys.forEach((el) => {
        fieldMappings[el.key] = {
          key: formatKey(el.value),
          displayName: el.value,
          visibility: el.visibility,
          required: el.required,
        };
      });

      let isValid = true;
      if (this.addresses[0]?.addressLine1?.displayName) {
        fieldMappings.addresses = this.addresses?.map((address) => {
          if (!address.addressLine1 || !address.addressLine1.displayName) {
            this.$toast.error(
              `Address Line 1 is required for ${address.typeOfAddress}.`
            );
            isValid = false;
            return;
          }

          let addressMapping = { typeOfAddress: address.typeOfAddress };
          Object.keys(this.addressFields)?.forEach((fieldKey) => {
            const field = address[fieldKey];
            const transformedKey = formatKey(field.displayName);
            addressMapping[fieldKey] = {
              key: transformedKey || fieldKey,
              displayName: field.displayName,
            };
          });
          return addressMapping;
        });

        const defaultAddress = this.addresses.find(
          (address) => address.default
        );

        fieldMappings.defaultAddress = defaultAddress
          ? defaultAddress.typeOfAddress
          : null;
      }

      fieldMappings["caseOutcomeStatus"] = {
        key: formatKey(this.caseOutcomeStatus),
        displayName: this.caseOutcomeStatus,
        visibility: true,
      };

      fieldMappings["status"] = {
        key: formatKey(this.caseStatusName),
        displayName: this.caseStatusName,
        visibility: true,
      };

      this.tableTabKeys.forEach((tk) => {
        const newKey = formatKey(tk.value);
        tableTabMapping[tk.key] = {
          text: tk.text,
          key: newKey,
          displayName: tk.value?.trim(),
        };
      });

      this.extraColumns.forEach((el) => {
        const display = el.value?.split("-");
        const newKey = display[0]?.trim().split(" ").join("_").toLowerCase();
        const type = display[1].trim();
        additionalColumns[newKey] = {
          key: newKey,
          displayName: display[0].trim(),
          fieldType: type,
        };
      });

      const existingSubCase = this.$props.enabledSubCases.flatMap(
        (sub) => sub.subCase
      );

      let enabledSubCases;

      if (this.$props.cloneSubCase?.cloneIndex) {
        if (this.checkDuplicateCaseType()) return;
        enabledSubCases = [
          {
            useCase: this.$props.useCase.toLowerCase(),
            subCase: [
              ...existingSubCase,
              {
                branch: this.selectedBranch || undefined,
                name: this.caseType.value,
                fieldMappings,
                additionalColumns,
                tableTabMapping,
                amountRefField: this.selectedAmountKey,
                userDefinedCaseStatus,
                questionnaire: this.subCaseQuestions || {},
                userFeedbackQuestionnaire: this.customerFeedback || {},
              },
            ],
          },
        ];
      }

      if (this.$props.editSubCase?.indx !== undefined) {
        existingSubCase[this.$props.editSubCase.indx] = {
          branch: this.selectedBranch || undefined,
          name: this.caseType.value,
          fieldMappings,
          additionalColumns,
          tableTabMapping,
          amountRefField: this.selectedAmountKey,
          userDefinedCaseStatus,
          questionnaire: this.subCaseQuestions || {},
          userFeedbackQuestionnaire: this.customerFeedback || {},
        };

        enabledSubCases = [
          {
            useCase: this.$props.useCase.toLowerCase(),
            subCase: existingSubCase,
          },
        ];
      } else {
        if (this.checkDuplicateCaseType()) return;
        enabledSubCases = [
          {
            useCase: this.$props.useCase.toLowerCase(),
            subCase: [
              ...existingSubCase,
              {
                branch: this.selectedBranch || undefined,
                name: this.caseType.value,
                fieldMappings,
                additionalColumns,
                tableTabMapping,
                amountRefField: this.selectedAmountKey,
                userDefinedCaseStatus,
                questionnaire: this.subCaseQuestions || {},
                userFeedbackQuestionnaire: this.customerFeedback || {},
              },
            ],
          },
        ];
      }
      if (!isValid) {
        return;
      }

      console.log(enabledSubCases, " enabled");
      this.$emit("orgMapKeys", enabledSubCases);
    },
    handleQuestions(val, index) {
      const status = this.catOptionTabs[index];
      if (!this.subCaseQuestions || Array.isArray(this.subCaseQuestions)) {
        this.subCaseQuestions = {};
      }
      this.$set(this.subCaseQuestions, status, val);
    },
    handleCloneAction(val, item, targetType) {
      this.clonedValue = val.value;
      this.clonedKey = item;
      this.cloneTarget = targetType;

      if (Object.keys(this[targetType]).includes(item)) {
        this.showConfirmationClone = true;
      } else {
        this.cloneNewQuestions();
      }
    },

    cloneWithExistingQuestions() {
      this[this.cloneTarget] = {
        ...this[this.cloneTarget],
        [this.clonedKey]: [
          ...(this[this.cloneTarget][this.clonedKey] || []),
          ...this.clonedValue,
        ],
      };
      this.resetCloneState();
    },

    cloneNewQuestions() {
      this[this.cloneTarget] = {
        ...this[this.cloneTarget],
        [this.clonedKey]: this.clonedValue,
      };
      this.resetCloneState();
    },

    resetCloneState() {
      this.showConfirmationClone = false;
      this.clonedValue = null;
      this.clonedKey = null;
      this.cloneTarget = null;
      this.$toast.success(`Questions copied successfully`);
    },

    handleFeedback(val, index) {
      const status = this.catOptionTabs[index];
      if (!this.customerFeedback || Array.isArray(this.customerFeedback)) {
        this.customerFeedback = {};
      }
      this.$set(this.customerFeedback, status, val);
    },
    async fetchBranches() {
      try {
        const response = await this.$axios.get("/workforce/branches");
        this.branches = response.data.branches;
      } catch (error) {
        console.error("Error fetching bracnhes:", error);
      }
    },
    onCancel() {
      this.orgKeys = defaultOrgKeys;
      this.$emit("cancel");
      this.$refs.form.reset();
      // this.$refs.formAddress.reset();
      this.extraColumns = [];
      this.categories = [
        {
          categoryName: "",
          options: [],
          optionsList: [],
          assignedTemplates: {},
        },
      ];
    },
    addCategory() {
      this.categories.push({
        categoryName: "",
        options: [],
        optionsList: [],
        assignedTemplates: {},
      });
    },
    checkForDuplicateAddressLine1() {
      const keyMap = new Map();

      this.addresses.forEach((address, index) => {
        const key = address.addressLine1.key;

        if (key && keyMap.has(key)) {
          this.alertMessage = `Duplicate addressLine1 key found in Address ${
            keyMap.get(key) + 1
          } and Address ${index + 1}`;
        } else {
          keyMap.set(key, index);
        }
      });
    },
    populateFields(subCase) {
      if (!subCase) {
        this.$refs?.form?.reset();
        this.orgKeys = [...defaultOrgKeys];
        this.extraColumns = [];
        this.categories = [];
        return;
      }

      this.subCaseQuestions = subCase?.questionnaire || {};
      this.customerFeedback = subCase?.userFeedbackQuestionnaire || {};
      const updatedOrgKeys = [...this.orgKeys];
      const fieldMappings = subCase.fieldMappings || {};
      const addressesArray = fieldMappings.addresses || [];
      this.selectedAmountKey = subCase.amountRefField || null;

      updatedOrgKeys.forEach((k) => {
        k.value = fieldMappings[k.key]?.displayName || "";
        k.visibility = fieldMappings[k.key]?.visibility
          ? fieldMappings[k.key]?.visibility
          : false;
        k.required = fieldMappings[k.key]?.required
          ? fieldMappings[k.key]?.required
          : false;
      });

      if (addressesArray.length) {
        const defaultAddressType = fieldMappings.defaultAddress || "";
        this.addresses = addressesArray.map((address) => ({
          ...address,
          default: address.typeOfAddress === defaultAddressType,
        }));
      }

      this.selectedBranch = subCase.branch || null;
      this.orgKeys = updatedOrgKeys;

      if (subCase.cloneIndex == 0 || subCase.cloneIndex) {
        this.caseType.value = "";
        this.existingCaseType = null;
      } else {
        this.caseType.value = subCase.name;
        this.existingCaseType = subCase.name;
      }
      this.caseOutcomeStatus =
        fieldMappings.caseOutcomeStatus?.displayName || "";
      this.caseStatusName = fieldMappings.status?.displayName || "";
      this.staticFields.crn = fieldMappings.crn?.displayName || "";
      this.staticFields.customerFullName =
        fieldMappings.customerFullName?.displayName || "";
      this.staticFields.customerMobile =
        fieldMappings.customerMobile?.displayName || "";
      this.staticFields.customerEmail =
        fieldMappings.customerEmail?.displayName || "";
      this.tat = fieldMappings.tat?.visibility;
      this.caseStartDate = fieldMappings.caseStartDate?.visibility;

      this.extraColumns = Object.keys(subCase.additionalColumns || {}).map(
        (k) => {
          const column = subCase.additionalColumns[k];
          const displayName = column.displayName;
          const fieldType = column.fieldType;
          let value = fieldType
            ? `${displayName} - ${fieldType}`
            : `${displayName} - STRING`;
          return {
            text: displayName,
            key: column.key,
            value: value,
            search: displayName,
          };
        }
      );

      this.tableTabKeys = subCase?.tableTabMapping
        ? Object.entries(subCase.tableTabMapping).map(([key, obj]) => ({
            text: obj.text,
            key,
            value: obj.displayName,
          }))
        : this.tableTabKeys.map((el) => ({
            text: el.text,
            key: el.key,
            value: el.text,
          }));

      this.categories = (subCase.userDefinedCaseStatus || []).map((el) => ({
        categoryName: el.categoryName || "Category 1",
        options: el.options || [],
        optionsList: el.optionsList || [],
        assignedTemplates: el.assignedTemplates || {},
      }));
    },
    checkDuplicateCaseType() {
      const existingSubCaseNames = this.$props.enabledSubCases.flatMap((sub) =>
        sub.subCase.map((s) => s.name.toLowerCase())
      );
      const isDuplicateCaseName = existingSubCaseNames.includes(
        this.caseType?.value?.toLowerCase()
      );

      if (isDuplicateCaseName) {
        this.$toast.error(
          "Case type name must be unique. A case with this name already exists."
        );
        return isDuplicateCaseName;
      }
    },

    validateAddress() {
      this.checkForDuplicateAddressLine1();
    },
    getFilteredOutcomeQuestions(questions, currentKey) {
      return Object.fromEntries(
        Object.entries(questions).filter(([key]) => key !== currentKey)
      );
    },
  },
  watch: {
    addresses: {
      deep: true,
      handler() {
        this.checkForDuplicateAddressLine1();
      },
    },
    editSubCase: {
      handler(subCase) {
        if (subCase) {
          this.populateFields(subCase);
        }
      },
      immediate: true,
    },
    cloneSubCase: {
      handler(subCase) {
        if (subCase) {
          this.populateFields(subCase);
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    await this.fetchBranches();
    this.subCaseQuestions = this.editSubCase?.questionnaire || [];
    this.customerFeedback = this.editSubCase?.userFeedbackQuestionnaire || [];
    this.checkForDuplicateAddressLine1();
  },
};
</script>
